import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as log from '../util/log';
import { storableError } from '../util/errors';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import { createUserWithIdp, subscribeToNewsletter } from '../util/api';
import { pushDataLayerEvent } from '../analytics/dataLayer';
import { clearSignupAttribution, signupAttributionParams } from '../analytics/signupAttribution';

// After a successful signup, ask the server to sync the newsletter opt-in
// to Mailchimp. Fire-and-forget: consent is re-checked server-side
// (privateData.acceptsMarketing) and any failure is handled + retried
// there, so this must never block or fail the signup flow.
const triggerNewsletterSubscribe = () => {
  try {
    subscribeToNewsletter().catch(() => {});
  } catch (e) {
    // ignore — signup must not be affected by the newsletter sync
  }
};

// Conversion signal for a brand-new account, read by Google Tag Manager.
// Called from the two signup wrappers at the bottom of this file rather than
// from a reducer, so it fires once, only after the account actually exists,
// and never for a returning user logging in. That holds for the social paths
// too: Vipps / Google / Facebook reach signupWithIdp only when the server has
// established there is no existing account and sent the visitor to the
// confirm step (see server/api/auth/loginWithIdp.js) — an existing user is
// authenticated server-side and redirected without ever touching this file.
//
// It carries how the account was made (`method`: email, vipps, facebook or
// google) and where the visitor came from: `signup_source` (campaign, footer
// or direct) and any UTM parameters and fbclid kept from the ad
// (analytics/signupAttribution.js), which are then forgotten.
//
// `event_id` is "signup-<new account's id>", one per sign-up (Guro, 13
// October). The "Meta - Sign Up" tag passes it as the Pixel's Event ID, so
// Meta counts its browser event and the server copy of it as one sign-up;
// a server event of our own could reuse it, since it comes from the account.
// By the time this runs, the sign-up has loaded the new account as the
// current user. Left out rather than guessed if the id isn't there.
const trackSignupConversion = (method, getState) => {
  const userId = getState?.()?.user?.currentUser?.id?.uuid;
  const eventIdMaybe = userId ? { event_id: `signup-${userId}` } : {};
  pushDataLayerEvent('sign_up', { method, ...eventIdMaybe, ...signupAttributionParams() });
  clearSignupAttribution();
};

const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Initial State ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

// ================ Async Thunks ================ //

const authInfoThunk = createAsyncThunk('auth/authInfo', (_, thunkAPI) => {
  const { extra: sdk } = thunkAPI;
  return sdk.authInfo().catch(e => {
    // Requesting auth info just reads the token from the token
    // store (i.e. cookies), and should not fail in normal
    // circumstances. If it fails, it's due to a programming
    // error. In that case we mark the operation done and dispatch
    // `null` success action that marks the user as unauthenticated.
    log.error(e, 'auth-info-failed');
    return null;
  });
});

const loginThunk = createAsyncThunk(
  'auth/login',
  ({ username, password }, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .login({ username, password })
      .then(() => {
        return dispatch(fetchCurrentUser({ afterLogin: true }));
      })
      .then(() => ({ username, password }))
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'loginInProgress')) {
        return false;
      }
    },
  }
);

const logoutThunk = createAsyncThunk(
  'auth/logout',
  (_, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk
      .logout()
      .then(() => {
        dispatch(clearCurrentUser());
        log.clearUserId();
        return true;
      })
      .catch(e => rejectWithValue(storableError(e)));
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'logoutInProgress')) {
        return false;
      }
    },
  }
);

const signupThunk = createAsyncThunk(
  'auth/signup',
  (params, thunkAPI) => {
    const { rejectWithValue, extra: sdk, dispatch } = thunkAPI;

    return sdk.currentUser
      .create(params)
      .then(() =>
        dispatch(loginThunk({ username: params.email, password: params.password })).unwrap()
      )
      .then(() => params)
      .catch(e => {
        log.error(e, 'signup-failed', {
          email: params.email,
          firstName: params.firstName,
          lastName: params.lastName,
        });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'signupInProgress')) {
        return false;
      }
    },
  }
);

const signupWithIdpThunk = createAsyncThunk(
  'auth/signupWithIdp',
  (params, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    return createUserWithIdp(params)
      .then(() => dispatch(fetchCurrentUser({ afterLogin: true })))
      .then(() => params)
      .catch(e => {
        log.error(e, 'create-user-with-idp-failed', { params });
        return rejectWithValue(storableError(e));
      });
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      if (authenticationInProgress(state, 'confirmInProgress')) {
        return false;
      }
    },
  }
);

// ================ Slice ================ //

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // Auth Info
    builder.addCase(authInfoThunk.fulfilled, (state, action) => {
      const payload = action.payload;
      state.authInfoLoaded = true;
      state.isAuthenticated = authenticated(payload);
      state.isLoggedInAs = loggedInAs(payload);
      state.authScopes = payload?.scopes || [];
    });

    // Login
    builder
      .addCase(loginThunk.pending, state => {
        state.loginInProgress = true;
        state.loginError = null;
        state.logoutError = null;
        state.signupError = null;
      })
      .addCase(loginThunk.fulfilled, state => {
        state.loginInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loginInProgress = false;
        state.loginError = action.payload;
      });

    // Logout
    builder
      .addCase(logoutThunk.pending, state => {
        state.logoutInProgress = true;
        state.loginError = null;
        state.logoutError = null;
      })
      .addCase(logoutThunk.fulfilled, state => {
        state.logoutInProgress = false;
        state.isAuthenticated = false;
        state.isLoggedInAs = false;
        state.authScopes = [];
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.logoutInProgress = false;
        state.logoutError = action.payload;
      });

    // Signup
    builder
      .addCase(signupThunk.pending, state => {
        state.signupInProgress = true;
        state.loginError = null;
        state.signupError = null;
      })
      .addCase(signupThunk.fulfilled, state => {
        state.signupInProgress = false;
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.signupInProgress = false;
        state.signupError = action.payload;
      });

    // Signup with IDP (Confirm)
    builder
      .addCase(signupWithIdpThunk.pending, state => {
        state.confirmInProgress = true;
        state.loginError = null;
        state.confirmError = null;
      })
      .addCase(signupWithIdpThunk.fulfilled, state => {
        state.confirmInProgress = false;
        state.isAuthenticated = true;
      })
      .addCase(signupWithIdpThunk.rejected, (state, action) => {
        state.confirmInProgress = false;
        state.confirmError = action.payload;
      });
  },
});

export { logoutThunk };
export default authSlice.reducer;

// ================ Selectors ================ //

export const authenticationInProgress = (state, nextInProgress = 'any') => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.auth;
  const anyInProgress =
    loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
  return nextInProgress === 'loginInProgress'
    ? loginInProgress || logoutInProgress || confirmInProgress
    : anyInProgress;
};

// ================ Returning-user flag ================ //
// Remember (per device) that someone has authenticated here, so a
// logged-out visitor tapping "profile" can be routed to Logg inn (login)
// rather than Opprett konto (signup). Client-only + best-effort; failures
// (private mode / disabled storage / SSR) are ignored.

const RETURNING_USER_KEY = 'zuprize_returning';

export const hasReturningUser = () => {
  try {
    return (
      typeof window !== 'undefined' && window.localStorage.getItem(RETURNING_USER_KEY) === 'true'
    );
  } catch (e) {
    return false;
  }
};

const markReturningUser = () => {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(RETURNING_USER_KEY, 'true');
    }
  } catch (e) {
    // ignore
  }
};

// ================ Thunk Wrappers ================ //
// These maintain the same API as the original thunks

export const login = (username, password) => dispatch => {
  return dispatch(loginThunk({ username, password }))
    .unwrap()
    .then(res => {
      markReturningUser();
      return res;
    });
};

export const logout = () => dispatch => {
  // Logging out is the clearest signal that this device belongs to an
  // existing user — flag it so their next visit lands on Logg inn.
  markReturningUser();
  return dispatch(logoutThunk()).unwrap();
};

export const signup = params => (dispatch, getState) => {
  return dispatch(signupThunk(params))
    .unwrap()
    .then(res => {
      markReturningUser();
      triggerNewsletterSubscribe();
      trackSignupConversion('email', getState);
      return res;
    });
};

export const signupWithIdp = params => (dispatch, getState) => {
  return dispatch(signupWithIdpThunk(params))
    .unwrap()
    .then(res => {
      markReturningUser();
      triggerNewsletterSubscribe();
      trackSignupConversion(params?.idpId || 'idp', getState);
      return res;
    });
};

export const authInfo = () => dispatch => {
  return dispatch(authInfoThunk()).unwrap();
};
